import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, forkJoin, of, timer, throwError } from 'rxjs';
import { map, catchError, retryWhen, take, concatMap, delay } from 'rxjs/operators';

export interface ITranslationResource {
  prefix: string;
  suffix: string;
}

/**
 * Loader customizado para carregar múltiplos arquivos de tradução
 * Isso permite dividir os arquivos i18n por módulo, tornando a aplicação mais leve
 */
export class MultiTranslateHttpLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private resources: ITranslationResource[]
  ) { }

  /**
   * Carrega todos os arquivos de tradução e mescla em um único objeto
   * Implementa retry logic para casos onde os arquivos ainda não estão disponíveis
   */
  getTranslation(lang: string): Observable<any> {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 200; // ms entre tentativas

    const requests = this.resources.map(resource => {
      const path = resource.prefix + lang + resource.suffix;
      
      // Adiciona header para evitar blockUI nas requisições de tradução
      const headers = { 'blockLoading': 'true' };
      
      return this.http.get(path, { headers }).pipe(
        // Retry até MAX_RETRIES vezes com delay entre tentativas
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, index) => {
              const retryAttempt = index + 1;
              
              // Se ainda temos tentativas disponíveis, tenta novamente após delay
              if (retryAttempt <= MAX_RETRIES) {
                console.warn(`Translation file not found (attempt ${retryAttempt}/${MAX_RETRIES + 1}): ${path}, retrying in ${RETRY_DELAY}ms...`);
                return timer(RETRY_DELAY);
              }
              
              // Se esgotaram as tentativas, propaga o erro para ser tratado pelo catchError
              console.error(`Translation file not found after ${MAX_RETRIES + 1} attempts: ${path}`, error);
              return throwError(() => error);
            })
          )
        ),
        catchError((error) => {
          // Fallback: se todas as tentativas falharam, retorna objeto vazio
          // Mas loga detalhes do erro para debug
          if (error && error.status) {
            console.warn(`Translation file not found (final fallback) [${error.status}]: ${path}`);
          } else {
            console.warn(`Translation file not found (final fallback): ${path}`, error);
          }
          return of({});
        })
      );
    });

    return forkJoin(requests).pipe(
      map(responses => {
        // Mescla todos os objetos de tradução em um único
        return responses.reduce((acc, response) => {
          return { ...acc, ...response };
        }, {});
      })
    );
  }
}

/**
 * Factory para criar o MultiTranslateHttpLoader
 * @param http HttpClient
 * @returns MultiTranslateHttpLoader configurado apenas com traduções comuns
 * 
 * NOTA: Com lazy loading ativado, apenas as traduções comuns são carregadas inicialmente.
 * As traduções específicas de cada módulo são carregadas sob demanda através do LazyTranslateService.
 */
export function MultiTranslateHttpLoaderFactory(http: HttpClient): MultiTranslateHttpLoader {
  return new MultiTranslateHttpLoader(http, [
    // Traduções compartilhadas (sempre carregadas no início)
    // IMPORTANTE: Usar caminho absoluto (com /) para evitar problemas com rotas relativas
    { prefix: '/app/modules/common/i18n/', suffix: `.json?v=${Date.now()}` },
    
    // Traduções de autenticação (carregadas no início pois são usadas antes do app principal)
    { prefix: '/app/modules/autenticacao/i18n/', suffix: `.json?v=${Date.now()}` }
    
    // LAZY LOADING: Todos os outros módulos serão carregados sob demanda
    // através do LazyTranslateService quando suas rotas forem acessadas
  ]);
}

/**
 * Factory LEGADO - carrega todas as traduções de uma vez (não recomendado)
 * Use apenas se precisar desabilitar o lazy loading temporariamente
 */
export function MultiTranslateHttpLoaderFactoryLegacy(http: HttpClient): MultiTranslateHttpLoader {
  return new MultiTranslateHttpLoader(http, [
    // Traduções compartilhadas (sempre carregadas)
    // IMPORTANTE: Usar caminho absoluto (com /) para evitar problemas com rotas relativas
    { prefix: '/app/modules/common/i18n/', suffix: `.json?v=${Date.now()}` },
    
    // Módulos específicos
    { prefix: '/app/modules/autenticacao/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/carteira/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/conta/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/configuracao/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/dash/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/documentacao/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/download-registro/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/elasticsearch/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/esbuilder/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/estrategia/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/filtro-query-padrao/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/fluxo-estrategia/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/indicador-provedor/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/lista/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/lista-integracao-cadastro/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/mia-scheduling/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/mia-widgets/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/notificacao/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/ordenacao-padrao/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/perfil/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/pesquisa/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/plano-tarifa-provedor/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/preferencias-usuario/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/provider/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/jornada/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/retorno/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/rotina/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/tarefa/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/template/i18n/', suffix: `.json?v=${Date.now()}` },
    { prefix: '/app/modules/usuario/i18n/', suffix: `.json?v=${Date.now()}` }
  ]);
}

